import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useIsAuthenticated } from '@azure/msal-react';
import API_CONFIG from '../config/api';

const SettingsPanel = ({ onDataDeleted }) => {
  const { makeAuthenticatedRequest } = useAuth();
  const isAuthenticated = useIsAuthenticated();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  const [storageInfo, setStorageInfo] = useState({ count: 0, totalSize: 0, loading: true });
  
  const STORAGE_LIMIT = 100 * 1024 * 1024; // 100MB in bytes

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getStoragePercentage = () => {
    return Math.min((storageInfo.totalSize / STORAGE_LIMIT) * 100, 100);
  };

  const fetchStorageInfo = async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await makeAuthenticatedRequest(`${API_CONFIG.BASE_URL}/data/storage-info`);
      const data = await response.json();
      
      if (data.success) {
        setStorageInfo({
          count: data.count,
          totalSize: data.totalSize,
          loading: false
        });
      }
    } catch (err) {
      console.error('Error fetching storage info:', err);
      setStorageInfo(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchStorageInfo();
  }, [isAuthenticated]);

  const handleDeleteAllData = async () => {
    if (!isAuthenticated) return;
    
    setIsDeleting(true);
    setDeleteResult(null);
    
    try {
      const response = await makeAuthenticatedRequest(
        `${API_CONFIG.BASE_URL}/data/datasets/user/delete-all`,
        { method: 'DELETE' }
      );
      
      const result = await response.json();
      
      if (result.success) {
        setDeleteResult({
          success: true,
          message: `Successfully deleted ${result.deletedCount} dataset(s)`
        });
        
        // Notify parent component to refresh data
        if (onDataDeleted) {
          onDataDeleted();
        }
        
        // Refresh storage info
        fetchStorageInfo();
      } else {
        setDeleteResult({
          success: false,
          message: result.error || 'Failed to delete data'
        });
      }
    } catch (err) {
      setDeleteResult({
        success: false,
        message: 'Error connecting to server: ' + err.message
      });
    } finally {
      setIsDeleting(false);
      setShowConfirmDialog(false);
    }
  };

  return (
    <div className="settings-panel">
      <div className="settings-section">
        <h4 className="settings-section-title">Storage Usage</h4>
        <div className="settings-item">
          <div className="settings-label">Synced Layers</div>
          <div className="settings-value">
            {storageInfo.loading ? 'Loading...' : `${storageInfo.count} dataset(s)`}
          </div>
        </div>
        <div className="settings-item">
          <div className="settings-label">Storage Used</div>
          <div className="settings-value">
            {storageInfo.loading ? 'Loading...' : (
              <>
                {formatFileSize(storageInfo.totalSize)} / {formatFileSize(STORAGE_LIMIT)}
                <span className="storage-percentage">({getStoragePercentage().toFixed(1)}%)</span>
              </>
            )}
          </div>
        </div>
        {!storageInfo.loading && (
          <div className="storage-bar">
            <div 
              className="storage-progress" 
              style={{ 
                width: `${getStoragePercentage()}%`,
                backgroundColor: getStoragePercentage() > 90 ? '#d13438' : getStoragePercentage() > 75 ? '#ff8c00' : '#107c10'
              }}
            ></div>
          </div>
        )}
      </div>

      <div className="settings-section">
        <h4 className="settings-section-title">Privacy & Data</h4>
        <div className="settings-item">
          <div className="settings-label">Synced Data</div>
          <div className="settings-value">
            <button
              className="btn-delete-data"
              onClick={() => setShowConfirmDialog(true)}
              disabled={!isAuthenticated || isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete All My Data'}
            </button>
          </div>
        </div>
        {deleteResult && (
          <div className={`settings-message ${deleteResult.success ? 'success' : 'error'}`}>
            {deleteResult.message}
          </div>
        )}
        <div className="settings-help">
          This will permanently delete all datasets you have synced from SharePoint. This action cannot be undone.
        </div>
      </div>

      <div className="settings-section">
        <h4 className="settings-section-title">About</h4>
        <div className="settings-item">
          <div className="settings-label">Application</div>
          <div className="settings-value">
            ShareShape
          </div>
        </div>
        <div className="settings-item">
          <div className="settings-label">Description</div>
          <div className="settings-value">
            SharePoint-integrated geospatial data visualization platform
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>⚠️ Delete All Synced Data</h3>
            <p>
              This will permanently delete all datasets you have synced from SharePoint. 
              This action cannot be undone.
            </p>
            <p><strong>Are you sure you want to continue?</strong></p>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setShowConfirmDialog(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="btn-delete-confirm"
                onClick={handleDeleteAllData}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Yes, Delete All Data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPanel;